import {
    createContext,
    useContext,
    useId,
    type ComponentPropsWithRef,
    type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { ScreenOverlay } from '@/React/Shared/Component/ScreenOverlay'
import { Icon, IconNameEnum } from '@/React/Shared/Component/Icon'
import { useFocusTrap } from '@/React/Shared/Hook/useFocusTrap'

interface ModalContextValue {
    onClose?: () => void
    titleId?: string
}

const ModalContext = createContext<ModalContextValue>({})

export interface ModalContainerProps extends Omit<
    ComponentPropsWithRef<'div'>,
    'title' | 'content'
> {
    show?: boolean
    onClose?: () => void
    children?: ReactNode
}

export function ModalContainer({
    show = false,
    onClose,
    children,
    className = '',
    ...restProps
}: ModalContainerProps) {
    const generatedTitleId = useId()
    const ariaLabel = restProps['aria-label']
    const titleId =
        (restProps['aria-labelledby'] as string | undefined) ??
        `modal-title-${generatedTitleId}`
    const ariaLabelledby =
        restProps['aria-labelledby'] ?? (ariaLabel ? undefined : titleId)
    const trapRef = useFocusTrap<HTMLDivElement>(show, onClose)

    if (!show) {
        return null
    }

    const modalContent = (
        <ModalContext.Provider value={{ onClose, titleId }}>
            <ScreenOverlay onClick={onClose} />

            <div
                ref={trapRef}
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-labelledby={ariaLabelledby}
                {...restProps}
                className={`z-50 fixed bottom-0 left-0 md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 h-auto w-full md:w-160 p-2 outline-none ${className}`.trim()}
            >
                <div className="bg-white rounded-xl space-y-4">{children}</div>
            </div>
        </ModalContext.Provider>
    )

    if (typeof document === 'undefined') {
        return modalContent
    }

    return createPortal(modalContent, document.body)
}

export interface ModalHeaderProps extends ComponentPropsWithRef<'div'> {
    showCloseButton?: boolean
    children?: ReactNode
}

export function ModalHeader({
    showCloseButton = true,
    children,
    className = '',
    ...restProps
}: ModalHeaderProps) {
    const { onClose } = useContext(ModalContext)

    return (
        <div
            {...restProps}
            className={`flex justify-between items-center px-6 pt-6 ${className}`.trim()}
        >
            <div className="font-semibold text-lg text-gray-800">
                {children ?? <div />}
            </div>
            {showCloseButton ? <ModalCloseButton onClick={onClose} /> : null}
        </div>
    )
}

export function ModalTitle({
    id,
    children,
    className = '',
    ...restProps
}: ComponentPropsWithRef<'div'>) {
    const { titleId } = useContext(ModalContext)
    return (
        <div
            id={id ?? titleId}
            {...restProps}
            className={`font-semibold text-lg text-gray-800 ${className}`.trim()}
        >
            {children}
        </div>
    )
}

export function ModalContent({
    children,
    className = '',
    ...restProps
}: ComponentPropsWithRef<'div'>) {
    return (
        <div
            {...restProps}
            className={`px-6 ${className}`.trim()}
        >
            {children}
        </div>
    )
}

export function ModalActions({
    children,
    className = '',
    ...restProps
}: ComponentPropsWithRef<'div'>) {
    if (!children) {
        return (
            <div
                {...restProps}
                className={`flex rounded-b-xl border-stone-200 ${className}`.trim()}
            />
        )
    }

    return (
        <div
            {...restProps}
            className={`flex rounded-b-xl border-t border-stone-200 justify-end gap-3 bg-stone-100 px-6 py-4 ${className}`.trim()}
        >
            {children}
        </div>
    )
}

export interface ModalCloseButtonProps extends ComponentPropsWithRef<'button'> {}

export function ModalCloseButton({
    className = '',
    onClick,
    ...restProps
}: ModalCloseButtonProps) {
    return (
        <button
            type="button"
            aria-label="Close"
            onClick={onClick}
            className={`text-gray-400 hover:text-gray-900 cursor-pointer p-1 rounded-lg focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none ${className}`.trim()}
            {...restProps}
        >
            <Icon
                name={IconNameEnum.Close}
                className="w-5 h-5"
                aria-hidden="true"
            />
        </button>
    )
}

export const Modal = Object.assign(ModalContainer, {
    Container: ModalContainer,
    Header: ModalHeader,
    Title: ModalTitle,
    Content: ModalContent,
    Actions: ModalActions,
    CloseButton: ModalCloseButton,
})
